import React, { useState, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Building2, Calendar, DollarSign, Grid3X3, FolderOpen } from 'lucide-react';
import { portfolioItems, projectReferences, completedProjects2024, galleryImages, companyInfo } from '../data/mock';

// Watermarked Image Component
const WatermarkedImage = ({ src, alt, className, style }) => (
  <div className="relative w-full h-full">
    <img src={src} alt={alt} className={className} style={style} loading="lazy" />
    <img 
      src={companyInfo.logo} 
      alt="Barchalo" 
      className="absolute bottom-2 right-2 h-8 sm:h-10 w-auto opacity-80 pointer-events-none"
    />
  </div>
);

const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('projects');
  const [galleryLightboxOpen, setGalleryLightboxOpen] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  const categories = ['All', 'Commercial', 'Residential'];

  const filteredItems = selectedCategory === 'All'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === selectedCategory);

  const openLightbox = useCallback((index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? filteredItems.length - 1 : prev - 1
    );
  }, [filteredItems.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev === filteredItems.length - 1 ? 0 : prev + 1
    );
  }, [filteredItems.length]);

  const openGalleryLightbox = useCallback((index) => {
    setCurrentGalleryIndex(index);
    setGalleryLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeGalleryLightbox = useCallback(() => {
    setGalleryLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const goToPreviousGallery = useCallback(() => {
    setCurrentGalleryIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  }, []);

  const goToNextGallery = useCallback(() => {
    setCurrentGalleryIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxOpen) {
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') goToPrevious();
        if (e.key === 'ArrowRight') goToNext();
      }
      if (galleryLightboxOpen) {
        if (e.key === 'Escape') closeGalleryLightbox();
        if (e.key === 'ArrowLeft') goToPreviousGallery();
        if (e.key === 'ArrowRight') goToNextGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    lightboxOpen,
    galleryLightboxOpen,
    closeLightbox,
    closeGalleryLightbox,
    goToPrevious,
    goToNext,
    goToPreviousGallery,
    goToNextGallery,
  ]);

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80"
            alt="Construction background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-white/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6 animate-fade-in">
              <div className="h-1 w-16 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Portfolio</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6 animate-fade-in-up animation-delay-200 uppercase tracking-tight">
              Our Previous Work
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed animate-fade-in-up animation-delay-400">
              Explore our portfolio of completed projects showcasing over 40 years of expertise in 
              formwork, reinforcement, and concrete works across the Western Cape.
            </p>
          </div>
        </div>
      </section>

      {/* Featured: Woodgrain Concrete Finish */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-12 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Featured Technique</span>
              <div className="h-1 w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-tight">
              Woodgrain Concrete Finish
            </h2>
          </div>

          {/* Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-lg relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_87a42c2d-b3ff-4965-9d7d-e392365dc691/artifacts/cuc0y1ya_d3286a12-a575-4b05-b7ac-2521ce5d20f8.jpeg"
                alt="Woodgrain Concrete Finish - View 1"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-8 sm:h-10 w-auto opacity-80" />
            </div>
            <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-lg relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_87a42c2d-b3ff-4965-9d7d-e392365dc691/artifacts/vexf5v6a_d3286a12-a575-4b05-b7ac-2521ce5d20f8.jpeg"
                alt="Woodgrain Concrete Finish - View 2"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-8 sm:h-10 w-auto opacity-80" />
            </div>
            <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-lg relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_87a42c2d-b3ff-4965-9d7d-e392365dc691/artifacts/s9ja610q_dd745dc8-d345-4541-b23b-c20e1501f001.jpeg"
                alt="Woodgrain Concrete Finish - View 3"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-8 sm:h-10 w-auto opacity-80" />
            </div>
          </div>

          {/* Description */}
          <div className="max-w-4xl mx-auto">
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              A woodgrain finish in concrete is a decorative technique used to replicate the natural texture and appearance of timber on cast concrete surfaces. It combines the strength and durability of concrete with the warmth and aesthetic appeal of wood, making it popular in both architectural and landscaping applications.
            </p>
            
            <div className="bg-slate-50 border-l-4 border-yellow-500 p-6 rounded-sm">
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-3 uppercase">
                What is a Woodgrain Concrete Finish?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                This finish is achieved by imprinting or casting the texture of real wood onto wet concrete using timber planks, rubber molds, or specially designed form liners. Once the concrete cures and the formwork is removed, the surface retains the detailed grain, knots, and imperfections typical of natural wood.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-slate-200 sticky top-16 sm:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('projects')}
              data-testid="tab-projects"
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-bold uppercase tracking-wide transition-all duration-300 border-b-4 text-sm sm:text-base min-h-[48px] ${
                activeTab === 'projects'
                  ? 'text-yellow-600 border-yellow-500'
                  : 'text-slate-500 border-transparent hover:text-slate-900'
              }`}
            >
              <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5" />
              Projects
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              data-testid="tab-gallery"
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-bold uppercase tracking-wide transition-all duration-300 border-b-4 text-sm sm:text-base min-h-[48px] ${
                activeTab === 'gallery'
                  ? 'text-yellow-600 border-yellow-500'
                  : 'text-slate-500 border-transparent hover:text-slate-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4 sm:w-5 sm:h-5" />
              Gallery
            </button>
          </div>
        </div>
      </section>

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <>
          <section className="py-16 sm:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 uppercase">Featured Projects</h2>
                <p className="text-slate-600">Major projects completed with industry-leading contractors</p>
              </div>

              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    data-testid={`filter-${category.toLowerCase()}`}
                    className={`px-6 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide transition-all duration-300 ${
                      selectedCategory === category
                        ? 'bg-yellow-500 text-slate-900'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredItems.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => openLightbox(index)}
                    data-testid={`project-card-${item.id}`}
                    className="group relative aspect-[4/3] rounded-sm overflow-hidden cursor-pointer bg-slate-100 shadow-md hover:shadow-xl transition-shadow duration-300"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-8 sm:h-10 w-auto opacity-80 z-10" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/80 transition-all duration-300" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-6">
                      <span className="inline-block px-5 py-2 bg-yellow-500 text-slate-900 text-sm font-bold uppercase rounded-sm mb-4">
                        {item.category}
                      </span>
                      <h3 className="text-white font-bold text-2xl text-center mb-3">{item.title}</h3>
                      {item.client && (
                        <p className="text-white/90 text-base mb-2 font-medium">{item.client}</p>
                      )}
                      {item.value && (
                        <p className="text-yellow-400 font-bold text-xl mt-2">{item.value}</p>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900 to-transparent md:hidden">
                      <span className="inline-block px-2 py-0.5 bg-yellow-500 text-slate-900 text-xs font-bold uppercase rounded-sm mb-2">
                        {item.category}
                      </span>
                      <h3 className="text-white font-medium text-sm">{item.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 sm:py-24 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="h-1 w-12 bg-yellow-500" />
                  <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">References</span>
                  <div className="h-1 w-12 bg-yellow-500" />
                </div>
                <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 uppercase">Current Project References</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                  Contact our clients directly to learn about our work quality and professionalism.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {projectReferences.map((ref) => (
                  <div
                    key={ref.id}
                    className="p-6 bg-white border border-slate-100 rounded-sm hover:border-yellow-500/50 transition-all duration-300 shadow-sm hover:shadow-lg"
                  >
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{ref.title}</h3>
                    <p className="text-yellow-600 text-sm font-bold mb-4">{ref.location}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2 className="w-4 h-4 text-yellow-500" />
                        <span>{ref.client}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <span className="text-slate-500">Contact:</span>
                        <span>{ref.contact}</span>
                      </div>
                      <a 
                        href={`tel:${ref.phone}`}
                        className="flex items-center gap-2 text-slate-700 hover:text-yellow-600 transition-colors"
                      >
                        <span>{ref.phone}</span>
                      </a>
                      {ref.website && (
                        <a
                          href={`https://${ref.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-yellow-600 hover:text-yellow-500 font-medium transition-colors break-all"
                        >
                          <span>{ref.website}</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 uppercase">Photo Gallery</h2>
              <p className="text-slate-600">A collection of our construction work and completed projects</p>
            </div>

            {galleryImages && galleryImages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((image, index) => (
                  <div
                    key={image.src}
                    onClick={() => openGalleryLightbox(index)}
                    className="group relative aspect-square rounded-sm overflow-hidden cursor-pointer bg-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300"
                  >
                    <img
                      src={image.src}
                      alt={image.caption || `Gallery image ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-6 sm:h-8 w-auto opacity-80 z-10" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-all duration-300" />
                    {image.caption && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4">
                        <p className="text-white text-sm text-center font-medium">{image.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-slate-50 rounded-sm border border-slate-200">
                <Grid3X3 className="w-16 h-16 text-yellow-500/30 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Gallery Coming Soon</h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  We're adding more photos of our construction work. Check back soon for an expanded gallery.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Gallery Lightbox */}
      {galleryLightboxOpen && galleryImages && galleryImages[currentGalleryIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeGalleryLightbox}
        >
          <button
            onClick={closeGalleryLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToPreviousGallery(); }}
            className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToNextGallery(); }}
            className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="max-w-5xl max-h-[85vh] mx-2 sm:mx-4 w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[currentGalleryIndex].src}
              alt={galleryImages[currentGalleryIndex].caption || ''}
              className="max-w-full max-h-[70vh] sm:max-h-[75vh] object-contain rounded-sm mx-auto"
            />
            {galleryImages[currentGalleryIndex].caption && (
              <p className="text-center text-white mt-3 sm:mt-4 text-sm sm:text-lg px-4">{galleryImages[currentGalleryIndex].caption}</p>
            )}
          </div>

          <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 bg-slate-900 px-4 py-2 rounded-sm text-white text-sm font-bold">
            {currentGalleryIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      {/* Projects Lightbox */}
      {lightboxOpen && filteredItems[currentImageIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div
            className="max-w-5xl max-h-[85vh] mx-2 sm:mx-4 w-full bg-white rounded-sm overflow-hidden shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filteredItems[currentImageIndex].image}
              alt={filteredItems[currentImageIndex].title}
              className="w-full max-h-[40vh] sm:max-h-[50vh] object-cover"
            />
            <div className="p-5 sm:p-8 text-center">
              <span className="inline-block px-4 py-1.5 bg-yellow-500 text-slate-900 text-xs font-bold uppercase rounded-sm mb-3 sm:mb-4">
                {filteredItems[currentImageIndex].category}
              </span>
              <h3 className="text-slate-900 font-bold text-xl sm:text-2xl mb-2 sm:mb-3">
                {filteredItems[currentImageIndex].title}
              </h3>
              <p className="text-slate-600 text-sm sm:text-base mb-4 sm:mb-6 max-w-2xl mx-auto">
                {filteredItems[currentImageIndex].description}
              </p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm">
                {filteredItems[currentImageIndex].client && (
                  <div className="flex items-center gap-2 text-slate-700 bg-slate-100 px-4 py-2 rounded-sm">
                    <Building2 className="w-4 h-4 text-yellow-500" />
                    <span>{filteredItems[currentImageIndex].client}</span>
                  </div>
                )}
                {filteredItems[currentImageIndex].value && (
                  <div className="flex items-center gap-2 text-slate-700 bg-slate-100 px-4 py-2 rounded-sm">
                    <DollarSign className="w-4 h-4 text-yellow-500" />
                    <span>{filteredItems[currentImageIndex].value}</span>
                  </div>
                )}
                {filteredItems[currentImageIndex].completionDate && (
                  <div className="flex items-center gap-2 text-slate-700 bg-slate-100 px-4 py-2 rounded-sm">
                    <Calendar className="w-4 h-4 text-yellow-500" />
                    <span>{filteredItems[currentImageIndex].completionDate}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 bg-slate-900 px-4 py-2 rounded-sm text-white text-sm font-bold">
            {currentImageIndex + 1} / {filteredItems.length}
          </div>
        </div>
      )}
    </main>
  );
};

export default PortfolioPage;
